import { Box } from "@mui/material";
import type { FC, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { type LocationEditorDesign, regularFieldSx, type V1OnlyLocationEditorDesign } from "./consts";

type CommonProps = {
	/** Synergy calls the section a Region and the location a Facility. */
	isSynergyMS: boolean;
	sectionOptions: string[];
	selectedSection: string;
	onSectionChange: (section?: string) => void;
	onAddSection: (section: string) => void;
	onDropdownOpen: () => void;
	onDropdownClose: () => void;
};

/** The Schedule Template popup: the section beside the location's name. */
type V1Props = {
	design: V1OnlyLocationEditorDesign;
	/** Hosts that name the location themselves hide the name field. */
	showNameField: boolean;
	nameOptions: string[];
	name: string;
	/** Picking an existing name and creating a new one both land here. */
	onNameChange: (name: string | undefined) => void;
};

/**
 * The Locations page's expanded editor: the section alone, since the page names the location itself (its band and
 * Rename). v2 alone has a read-only state: the editor under a Schedule Template edit lock.
 */
type V2Props = { design: Extract<LocationEditorDesign, "v2">; disabled: boolean };

type Props = CommonProps & (V1Props | V2Props);

/** What the location is called and the section (or region) it sits in. */
export const LocationIdentityFields: FC<Props> = (props): ReactElement => {
	const { isSynergyMS, sectionOptions, selectedSection, onSectionChange, onAddSection, onDropdownOpen, onDropdownClose } = props;
	const { t } = useTranslation();
	switch (props.design) {
		case "v1": {
			const { showNameField, nameOptions, name, onNameChange } = props;
			return (
				<Box
					sx={{
						display: "flex",
						gap: 1,
						width: "100%",
					}}
				>
					<Box key={"section-dropdown-box"} id={"section-dropdown-box"}>
						<SystemDropdown
							onOpen={onDropdownOpen}
							onClose={onDropdownClose}
							createNewOption={{
								enabled: true,
								label: t("schedule.template.v3.editor.addSection"),
								onCreate: onAddSection,
							}}
							label={isSynergyMS ? t("schedule.template.v3.editor.region") : t("schedule.template.v3.editor.section")}
							selectSx={regularFieldSx}
							items={sectionOptions}
							selectedItem={selectedSection}
							onChange={onSectionChange}
						/>
					</Box>
					{showNameField && (
						<SystemDropdown
							onOpen={onDropdownOpen}
							onClose={onDropdownClose}
							createNewOption={{
								enabled: true,
								label: isSynergyMS ? t("schedule.template.facility.add") : t("schedule.template.location.name.add"),
								onCreate: onNameChange,
							}}
							label={isSynergyMS ? t("schedule.template.v3.editor.facility") : t("schedule.template.v3.editor.locationName")}
							selectSx={regularFieldSx}
							items={nameOptions}
							// Options are trimmed names, as Save writes them, so a stored name with stray spaces still shows.
							selectedItem={name.trim()}
							onChange={onNameChange}
							data-testid="location-name-dropdown"
						/>
					)}
				</Box>
			);
		}
		case "v2":
			return (
				<SystemDropdown
					onOpen={onDropdownOpen}
					onClose={onDropdownClose}
					createNewOption={{
						enabled: true,
						label: t("schedule.template.v3.editor.addSection"),
						onCreate: onAddSection,
					}}
					label={isSynergyMS ? t("schedule.template.v3.editor.region") : t("schedule.template.v3.editor.section")}
					// "No section" first: picking it saves the section as "", as a location created without one is saved.
					items={[
						{ value: "", label: isSynergyMS ? t("locations.section.noneRegion") : t("locations.section.none") },
						...sectionOptions.map((section) => ({ value: section, label: section })),
					]}
					selectedItem={selectedSection}
					// Without it the trigger draws "" as an empty field instead of the "No section" row.
					displayEmpty
					onChange={onSectionChange}
					fullWidth
					disabled={props.disabled}
					data-testid="location-section-dropdown"
				/>
			);
	}
};
